import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebouncedMemo } from '@hooks/debounceAsync';
import CatalogService from '@utility/services/catalog';
import {
  buildMappableParams,
  initialMappableFilter,
  type MappableFilter,
} from '../lib/mappable-params';
import { catalogMappingKeys } from '../lib/query-keys';
import type { MappableListing, MappingState } from '../lib/types';

const noListings: MappableListing[] = [];

/* Listings of every connection of the account, marketplace or storage provider, in one mapping state. */
export function useMappableListings(state: MappingState, initialSearch = '') {
  const [filter, setFilter] = useState<MappableFilter>(() =>
    initialMappableFilter(initialSearch),
  );

  /* Typing in the search box asks once it settles, not on every key. */
  const search = useDebouncedMemo<string>(
    () => filter.search,
    [filter.search],
    500,
    filter.search,
  );
  const params = buildMappableParams(state, {
    ...filter,
    search: search ?? '',
  });

  const query = useQuery({
    queryKey: catalogMappingKeys.mappable(params),
    queryFn: () => CatalogService.getMappableListings(params),
    placeholderData: keepPreviousData,
  });

  /* Any filter but the page starts the list over. */
  const filterChanged = <K extends keyof MappableFilter>(
    key: K,
    value: MappableFilter[K],
  ) => {
    setFilter(previous => ({
      ...previous,
      [key]: value,
      page: key === 'page' ? (value as number) : 1,
    }));
  };

  return {
    data: query.data?.data ?? noListings,
    loading: query.isPending,
    error: query.error,
    totalData: query.data?.total_data ?? 0,
    filter,
    filterChanged,
  };
}
